import { Alert, DatePicker, Form, Radio, Select } from "antd";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { FormModal } from "@/shared/components/FormModal";
import { useDebouncedValue } from "@/shared/hooks/useDebouncedValue";
import { useDepartmentOptions, useEmployeeOptions } from "../hooks/useSchedules";
import type {
	AssignmentFormValues,
	AssignmentTarget,
	Schedule,
	ScheduleAssignInput,
} from "../types";

const EMPLOYEE_SEARCH_DEBOUNCE_MS = 300;

interface AssignmentModalProps {
	open: boolean;
	schedule: Schedule | null;
	isSubmitting: boolean;
	onCancel: () => void;
	onSubmit: (input: ScheduleAssignInput) => void;
}

// Modul darajasida — `FormModal` `initialValues` HAVOLASI o'zgarganda formani
// qayta to'ldiradi. Inline literal har renderda yangi obyekt bo'lardi va xodim
// qidiruvida (`setEmployeeSearch`, `isFetching`) kiritilgan sanalar tozalanib
// ketardi. `NEW_SCHEDULE_VALUES` / `NEW_HOLIDAY_VALUES` bilan bir xil naqsh.
const NEW_ASSIGNMENT_VALUES = { target: "employee" } as const;

interface EmployeeFieldProps {
	options: { value: string; label: string }[];
	isLoading: boolean;
	onSearch: (value: string) => void;
}

function EmployeeField({ options, isLoading, onSearch }: EmployeeFieldProps) {
	const { t } = useTranslation("schedules");

	return (
		<Form.Item
			name="employeeId"
			label={t("assignmentModal.employee.label")}
			rules={[{ required: true, message: t("assignmentModal.employee.required") }]}
		>
			<Select
				placeholder={t("assignmentModal.employee.placeholder")}
				options={options}
				showSearch={{ filterOption: false, onSearch }}
				loading={isLoading}
				notFoundContent={isLoading ? t("employeeSelect.searching") : t("employeeSelect.notFound")}
			/>
		</Form.Item>
	);
}

function DepartmentField({ options }: { options: { value: string; label: string }[] }) {
	const { t } = useTranslation("schedules");

	return (
		<Form.Item
			name="departmentId"
			label={t("assignmentModal.department.label")}
			rules={[{ required: true, message: t("assignmentModal.department.required") }]}
		>
			<Select
				placeholder={t("assignmentModal.department.placeholder")}
				options={options}
				showSearch={{ optionFilterProp: "label" }}
			/>
		</Form.Item>
	);
}

export function AssignmentModal({
	open,
	schedule,
	isSubmitting,
	onCancel,
	onSubmit,
}: AssignmentModalProps) {
	const { t } = useTranslation("schedules");
	const [employeeSearch, setEmployeeSearch] = useState("");
	const debouncedEmployeeSearch = useDebouncedValue(employeeSearch, EMPLOYEE_SEARCH_DEBOUNCE_MS);

	const { data: employees = [], isFetching: isEmployeeSearchLoading } =
		useEmployeeOptions(debouncedEmployeeSearch);
	const { data: departments = [] } = useDepartmentOptions();

	const employeeOptions = employees.map((employee) => ({
		value: employee.id,
		label: employee.fullName,
	}));
	const departmentOptions = departments.map((department) => ({
		value: department.id,
		label: department.name,
	}));

	const targetOptions: { value: AssignmentTarget; label: string }[] = [
		{ value: "employee", label: t("assignmentModal.target.employee") },
		{ value: "department", label: t("assignmentModal.target.department") },
	];

	const handleSubmit = (values: AssignmentFormValues) => {
		if (!schedule) {
			return;
		}

		// XOR shu yerda hal bo'ladi: tanlanmagan tarmoqning qiymati YUBORILMAYDI.
		// Shu sabab foydalanuvchi tarmoqni almashtirganda eski maydonni
		// tozalash shart emas — yuborilmagan qiymat bazaga ham tushmaydi.
		// Backend ham ikkalasi kelganda rad etadi
		// (`messages.schedules.assignmentTargetRequired` + DB CHECK).
		onSubmit({
			scheduleId: schedule.id,
			employeeId: values.target === "employee" ? (values.employeeId ?? null) : null,
			departmentId: values.target === "department" ? (values.departmentId ?? null) : null,
			validFrom: values.validFrom.format("YYYY-MM-DD"),
			validTo: values.validTo ? values.validTo.format("YYYY-MM-DD") : null,
		});
	};

	return (
		<FormModal<AssignmentFormValues>
			open={open}
			okText={t("assignmentModal.submit")}
			title={
				schedule
					? t("assignmentModal.titleWithName", { name: schedule.name })
					: t("assignmentModal.createTitle")
			}
			initialValues={NEW_ASSIGNMENT_VALUES}
			isSubmitting={isSubmitting}
			onCancel={onCancel}
			onSubmit={handleSubmit}
		>
			<Alert
				className="mb-4"
				type="info"
				showIcon
				title={t("assignmentModal.info.title")}
				description={t("assignmentModal.info.description")}
			/>

			<Form.Item name="target" label={t("assignmentModal.targetLabel")}>
				<Radio.Group options={targetOptions} optionType="button" />
			</Form.Item>

			<Form.Item noStyle shouldUpdate={(prev, cur) => prev.target !== cur.target}>
				{({ getFieldValue }) =>
					getFieldValue("target") === "employee" ? (
						<EmployeeField
							options={employeeOptions}
							isLoading={isEmployeeSearchLoading}
							onSearch={setEmployeeSearch}
						/>
					) : (
						<DepartmentField options={departmentOptions} />
					)
				}
			</Form.Item>

			<Form.Item
				name="validFrom"
				label={t("assignmentModal.validFrom.label")}
				rules={[{ required: true, message: t("assignmentModal.validFrom.required") }]}
			>
				<DatePicker className="w-full" />
			</Form.Item>

			<Form.Item
				name="validTo"
				label={t("assignmentModal.validTo.label")}
				extra={t("assignmentModal.validTo.extra")}
			>
				<DatePicker className="w-full" />
			</Form.Item>
		</FormModal>
	);
}
